import React, { Suspense, useState, useRef, useEffect } from "react"
import { MapOptions, useMap } from "hooks/use_map"
import { CITIES } from "app/features/regional-map/regional-map.constants"
import usePaint from "hooks/use_paint"
import useFacets from "hooks/use_facets"
import useActiveFacet from "hooks/use_active_facet"
import { useAtomValue } from "jotai"
import { facetsAtom, hasUsedGeocoderAtom } from "app/state"
import {
  AGGREGATED_MUNICIPALITY_LAYER_NAME,
  BLOCKGROUP_LAYER_NAME,
  BLOCKGROUP_ZOOM,
  MUNICIPALITY_LAYER_NAME,
  MUNICIPALITY_ZOOM,
  NATIONAL_EXTENT,
} from "app/constants"
import SideBar, { MapHeader } from "components/sidebar"
import addNationalExplorerMapLayers from "lib/add_national_explorer_map_layers"
import useFilters from "hooks/use_filters"
import useMapTooltip from "hooks/use_map_tooltip"
import { HelpWidget } from "app/features/help-widget"
import NavMenu from "components/nav_menu"
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon"
import GeoCoder from "components/geocoder"
import { useMediaQuery } from "@material-ui/core"
import { ReportFinderTrigger } from "./components/report-finder-trigger"
import { useTranslation } from "react-i18next"
import { useCurrentLanguage } from "app/features/i18n/i18n.hooks"

const OPTIONS: Omit<MapOptions, "extent"> = {
  rememberPosition: true,
  zoomControl: true,
  hash: true,
}

export default function NationalMap() {
  const mapDiv = useRef<HTMLDivElement>(null)
  const nationalLocation = CITIES.find((city) => city.id === "national")!
  const facetsLoaded = useFacets(nationalLocation)
  const facets = useAtomValue(facetsAtom)!
  const isMobile = useMediaQuery("(max-width: 640px)")
  const [gettingStartedVisible, setGettingStartedVisible] = useState(true)
  const { t } = useTranslation(["common"])
  const { map, currentLevel, baseMapStyleIsLoaded, baseMapStyle, setBaseMapStyle } = useMap({
    mapDiv,
    scenario: undefined,
    options: {
      ...OPTIONS,
      extent: NATIONAL_EXTENT,
    },
    facetsLoaded: facetsLoaded,
    city: nationalLocation,
    addMapLayers: addNationalExplorerMapLayers,
  })

  const { activeFacet, setActiveFacet } = useActiveFacet({
    map: map,
    defaultValue: facets[0]!,
    zoomLevel: currentLevel,
    baseMapStyleIsLoaded,
    facetsLoaded,
  })

  usePaint({
    map,
    scenario: undefined,
    baseMapStyle,
    baseMapStyleIsLoaded,
    layers: [BLOCKGROUP_LAYER_NAME, MUNICIPALITY_LAYER_NAME],
  })

  useFilters({ map, baseMapStyleIsLoaded, currentLevel })
  useMapTooltip({ map: map!, facet: activeFacet })
  useCurrentLanguage()

  const hasUsedGeocoder = useAtomValue(hasUsedGeocoderAtom)

  const [hasZoomed, setHasZoomed] = useState(false)

  useEffect(() => {
    if (hasZoomed || hasUsedGeocoder) setGettingStartedVisible(false)
  }, [hasZoomed, hasUsedGeocoder])

  /** zoom into next layers if clicking on aggregated municipality or municipality */
  useEffect(() => {
    if (!map) return
    map.on("click", AGGREGATED_MUNICIPALITY_LAYER_NAME, (e) => {
      map.flyTo({
        center: e.lngLat,
        zoom: MUNICIPALITY_ZOOM,
        essential: true,
      })
    })

    map.on("click", MUNICIPALITY_LAYER_NAME, (e) => {
      map.flyTo({
        center: e.lngLat,
        zoom: BLOCKGROUP_ZOOM,
        essential: true,
      })
    })

    map.on("zoom", () => {
      setHasZoomed(true)
    })
  }, [map])

  return (
    <>
      <div className="inset-0 flex min-h-screen flex-col sm:h-screen sm:flex-row">
        {!isMobile && (
          <div className="flex flex-col divide-y divide-gray-300 overflow-y-auto border-gray-400 bg-[#FCFDFE] transition-all sm:w-96 sm:border-r sm:shadow-md">
            <MapHeader city={nationalLocation} />
            <Suspense fallback={null}>
              <GeoCoder map={map} city={nationalLocation} />
            </Suspense>
            <SideBar
              map={map}
              city={nationalLocation}
              activeFacet={activeFacet}
              setActiveFacet={setActiveFacet}
              baseMapStyle={baseMapStyle}
              setBaseMapStyle={setBaseMapStyle}
              currentLevel={currentLevel}
            />
          </div>
        )}

        {isMobile && (
          <div>
            <MapHeader city={nationalLocation} />
            <Suspense fallback={null}>
              <GeoCoder map={map} city={nationalLocation} />
            </Suspense>
          </div>
        )}

        <div ref={mapDiv} aria-label="National map" className="h-[50vh] sm:h-auto sm:flex-auto">
          <HelpWidget className="absolute bottom-10 z-10 hidden lg:block" />

          <div className="pointer-events-none absolute inset-0 z-10 flex w-full flex-row justify-center">
            {/** Get Started Modal should disappear if the user zooms, interacts with the map, or uses the geocoder */}
            {gettingStartedVisible && (
              <div className="pointer-events-auto hidden h-fit w-fit xl:block">
                <GetStartedModal />
              </div>
            )}

            <div className="pointer-events-auto absolute right-2 top-3 flex h-[36px] flex-row">
              <div className="relative mr-2 flex items-center rounded-full bg-white px-2 shadow-md">
                <ReportFinderTrigger currentLevel={currentLevel} />
              </div>
              <div className="flex items-center rounded-full bg-white px-2 shadow-md">
                <NavMenu displayText={t("common:menu")} />
              </div>
            </div>
          </div>
        </div>

        {isMobile && (
          <div className="h-full">
            <SideBar
              map={map}
              city={nationalLocation}
              activeFacet={activeFacet}
              setActiveFacet={setActiveFacet}
              baseMapStyle={baseMapStyle}
              setBaseMapStyle={setBaseMapStyle}
              currentLevel={currentLevel}
            />
          </div>
        )}
      </div>
    </>
  )
}

function GetStartedModal() {
  const { t } = useTranslation(["map"])
  const [open, setOpen] = useState(true)

  return (
    <>
      {open && (
        <div className="z-11 relative mt-2 h-fit w-fit rounded-lg bg-white shadow-xl">
          <button
            onClick={() => {
              setOpen(false)
            }}
            className="absolute right-2 top-2 h-4 w-4 hover:fill-gray-800"
          >
            <XMarkIcon className="fill-gray-600 hover:fill-gray-800" />
          </button>

          <div className="flex flex-col items-center justify-between p-6">
            <div className="text-caption font-bold uppercase text-brand-green-dark">
              {t("map:get_started")}
            </div>

            <div className="flex flex-col items-center justify-center text-base text-gray-600">
              <p>{t("map:get_started_zoom")}</p>
              <p>{t("map:get_started_explore")}</p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
